import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Menu, X } from 'lucide-react'
import logo from '../assets/solidlabs-logo.png'
import { useT, useLanguage } from '../i18n/LanguageContext'

export default function Navbar() {
  const t = useT()
  const { language, setLanguage } = useLanguage()
  const [scrolled, setScrolled] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // Mobile drawer: close menu, then scroll to anchor after layout settles.
  // Default <a href="#...">  navigation is unreliable while AnimatePresence
  // is exiting (menu height collapses mid-navigation, scroll lands wrong).
  const handleMobileLinkClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    href: string
  ) => {
    e.preventDefault()
    setMobileOpen(false)
    // Wait for drawer collapse animation (~300ms) before scrolling.
    setTimeout(() => {
      const target = document.querySelector(href)
      target?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 320)
  }

  const links = [
    { label: t.nav.about, href: '#about' },
    { label: t.nav.product, href: '#product' },
    { label: t.nav.oferta, href: '#oferta' },
    { label: t.nav.realizacje, href: '#realizacje' },
    { label: t.nav.contact, href: '#contact' },
  ]

  return (
    <motion.nav
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-[#050507]/80 backdrop-blur-2xl border-b border-white/[0.06]'
          : 'bg-transparent'
      }`}
    >
      <div
        className={`max-w-7xl mx-auto px-6 flex items-center justify-between transition-all duration-500 ${
          scrolled ? 'h-20' : 'h-28'
        }`}
      >
        <a
          href="#"
          aria-label={t.nav.logoAria}
          className="flex items-center group"
        >
          <img
            src={logo}
            alt={t.nav.logoAlt}
            className={`w-auto brightness-0 invert opacity-90 group-hover:opacity-100 transition-all duration-500 ${
              scrolled ? 'h-14' : 'h-24'
            }`}
          />
        </a>

        <div className="hidden md:flex items-center gap-1">
          {links.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="px-4 py-2 text-base text-zinc-400 hover:text-white transition-colors rounded-lg hover:bg-white/[0.04]"
            >
              {link.label}
            </a>
          ))}

          <div className="ml-3 flex items-center gap-1 text-sm">
            <button
              type="button"
              onClick={() => setLanguage('pl')}
              aria-current={language === 'pl' ? 'true' : undefined}
              aria-label={t.nav.langPlAria}
              className={`px-1.5 py-1 transition-colors ${
                language === 'pl'
                  ? 'text-white font-medium'
                  : 'text-zinc-500 hover:text-zinc-300'
              }`}
            >
              PL
            </button>
            <span aria-hidden="true" className="text-zinc-700 select-none">·</span>
            <button
              type="button"
              onClick={() => setLanguage('en')}
              aria-current={language === 'en' ? 'true' : undefined}
              aria-label={t.nav.langEnAria}
              className={`px-1.5 py-1 transition-colors ${
                language === 'en'
                  ? 'text-white font-medium'
                  : 'text-zinc-500 hover:text-zinc-300'
              }`}
            >
              EN
            </button>
          </div>

          <a
            href="https://spotto.pl"
            target="_blank"
            rel="noopener noreferrer"
            className="ml-3 px-5 py-2 text-base font-medium text-white bg-brand-700 hover:bg-brand-600 border border-white/[0.08] hover:border-white/[0.14] rounded-lg transition-all"
          >
            {t.nav.spottoApp}
          </a>
        </div>

        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label={mobileOpen ? t.nav.mobileToggleClose : t.nav.mobileToggleOpen}
          className="md:hidden p-2 text-zinc-400 hover:text-white"
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-[#0a0a0f]/95 backdrop-blur-2xl border-b border-white/[0.06] overflow-hidden"
          >
            <div className="px-6 py-4 flex flex-col gap-1">
              {links.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => handleMobileLinkClick(e, link.href)}
                  className="px-4 py-3 text-zinc-300 hover:text-white rounded-lg hover:bg-white/[0.04] transition-colors"
                >
                  {link.label}
                </a>
              ))}
              <a
                href="https://spotto.pl"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 px-5 py-3 text-center font-medium text-white bg-brand-700 hover:bg-brand-600 border border-white/[0.08] rounded-lg"
              >
                {t.nav.spottoApp}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  )
}
